<template>
  <ClientOnly>
    <div
      v-bind="$attrs"
      :class="[
        $style['like-button'],
        { [$style['like-button--liked']] : liked},
        { [$style[`like-button--${size}`]]: size }
      ]"
      @click.prevent.stop="toggleWishlistItem">
      <Btn
        :size="size"
        shape="round"
        :color="liked ? 'gold' : null"
        :aria-label="ariaLabel">
        <SvgIcon name="System/heart-line" :class="$style['like-button__icon']" />
      </Btn>

      <SvgIcon
        v-for="n in 20"
        :key="n"
        :class="$style['like-button__heart']"
        :style="{
          'animation-delay': getRand(n * 100, n * 150) + '0ms'
        }"
        name="System/heart-line" />
    </div>
    <template #fallback>
      <div
        v-bind="$attrs"
        :class="[
          $style['like-button'],
          { [$style[`like-button--${size}`]]: size }
        ]">
        <Btn
          :size="size"
          shape="round"
          :aria-label="ariaLabel">
          <SvgIcon name="System/heart-line" :class="$style['like-button__icon']" />
        </Btn>
      </div>
    </template>
  </ClientOnly>
</template>
<script setup>
const { $lang } = useNuxtApp()
const { showToast } = useToast()
const wishlistStore = useWishlistStore()

const props = defineProps({
  ariaLabel: {
    type: String,
    default: null
  },
  product: {
    type: Object,
    default: null
  },
  size: {
    type: String,
    default: 'large'
  },
  noAnimate: {
    type: Boolean,
    default: false
  }
})

const liked = computed(() => {
  if (!props.product?.cId) { return false }
  return wishlistStore.items.some(row => {
    return (typeof row === 'object' && row.product.cId === props.product.cId) || row === props.product.cId
  })
})

const getRand = (min, max) => {
  return Math.random() * (max - min) + min
}

const addToWishlist = async () => {
  await wishlistStore.add(props.product)
  showToast({
    message: $t('account/product-added-to-wishlist'),
    impact: 'default',
    icon: 'System/heart-line',
    secondaryAction: {
      to: { path: `/${$lang}/wishlist` },
      text: $t('account/show-wishlist')
    }
  })
  await wishlistStore.getProducts()
}

const removeFromWishlist = async () => {
  await wishlistStore.remove(props.product)
  liked.value = false
}

const toggleWishlistItem = () => {
  if (liked.value) {
    removeFromWishlist()
    return
  }
  addToWishlist()
}
</script>
<style lang="scss" module>
.like-button {
  position: relative;
  max-width: $spacing * 7;

  button {
    z-index: 2;
  }

  &__heart {
    position: absolute;
    left: 10px;
    top: 10px;
    z-index: 1;
    transform: scale(0.5);
    color: $gold;
  }

  &--small {
    .like-button__icon {
        height: $spacing * 2.5;
        width: $spacing * 2.5;
    }
  }

  &--liked {
    .like-button__nested {
      animation: heartbeat 1s $transition-easing;
      animation-iteration-count: 3;
    }

    &:has(.like-button__heart){
      .like-button__heart {
        &:nth-child(even) :local {
          animation: flyEven 2s $transition-ease-in;
          animation-iteration-count: 2;
        }

        &:nth-child(odd) :local {
          animation: flyOdd 2s $transition-ease-in;
          animation-iteration-count: 2;
        }
      }
    }
  }

  &:not(.like-button--liked){
    .like-button__nested{
      &:hover{
        background-color: $gold-light;
      }

      &:active{
        background-color: $gold;
      }
    }
  }
}

@keyframes flyEven {
  0% {
    opacity: 1;
    transform: scale(0.5) translate3d(0, 0, 0);
  }

  100% {
    transform: scale(1.5) translate3d(-20px, -100px, 0);
    opacity: 0;
    filter: blur(2px);
  }
}

@keyframes flyOdd {
  0% {
    opacity: 1;
    transform: scale(0.4) translate3d(0, 0, 0);
  }

  100% {
    transform: scale(1.6) translate3d(30px, -100px, 0);
    opacity: 0;
    filter: blur(2px);
  }
}

@keyframes heartbeat {
  0% {
    transform: scale(1);
  }

  20% {
    transform: scale(1.1);
  }

  40% {
    transform: scale(1);
  }

  60% {
    transform: scale(1.1);
  }

  80% {
    transform: scale(1);
  }

  100% {
    transform: scale(1);
  }
}
</style>
